import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import MaskedReveal from "@/components/site/MaskedReveal";
import Stars from "@/components/site/Stars";

const ROOM_IMG =
  "https://images.unsplash.com/photo-1630756510443-00031b7e9cad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwY29uY3JldGUlMjByZXRhaWwlMjBzdG9yZSUyMGludGVyaW9yfGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc4NDE2NTU3NHww&ixlib=rb-4.1.0&q=85";

export default function Showroom() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.15, 1, 1.35]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "10%"]);
  const leftX = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const rightX = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const captionY = useTransform(scrollYProgress, [0, 1], ["30%", "-30%"]);

  return (
    <section
      id="showroom"
      ref={ref}
      data-testid="showroom-section"
      className="relative min-h-[140vh] w-full overflow-hidden bg-black text-white"
    >
      {/* Faint starfield behind the sticky room */}
      <div aria-hidden className="absolute inset-0 opacity-70">
        <Stars density={0.9} drift={0.008} />
      </div>
      {/* Left side content that slides away */}
      <motion.div
        style={{ x: leftX }}
        className="absolute top-1/2 -translate-y-1/2 left-6 md:left-10 z-20 max-w-xs"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
          Room 01 / Concrete
        </div>
        <MaskedReveal
          as="h3"
          trigger="view"
          className="font-display text-4xl md:text-6xl uppercase leading-[0.9] tracking-tight"
          lines={["A room", "of quiet", "objects."]}
        />
      </motion.div>

      {/* Right side content that slides away */}
      <motion.div
        style={{ x: rightX }}
        className="absolute top-1/2 -translate-y-1/2 right-6 md:right-10 z-20 max-w-xs text-right"
      >
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-4">
          Walk further
        </div>
        <p className="text-white/70 text-sm md:text-base leading-relaxed">
          Every capsule is staged as an installation. Cotton, leather, glass,
          starlight — arranged for you to walk through and find your purpose.
        </p>
      </motion.div>

      {/* Center image that scales up (walking into it) */}
      <div className="sticky top-0 h-screen flex items-center justify-center">
        <motion.div
          style={{ scale }}
          className="relative aspect-[4/5] w-[54vmin] md:w-[46vmin] border border-white/15 overflow-hidden"
        >
          <motion.img
            style={{ y: imgY }}
            src={ROOM_IMG}
            alt="RIVH concrete showroom"
            className="w-full h-[120%] object-cover grayscale"
          />
          <div className="absolute inset-0 border border-white/10 pointer-events-none" />

          {/* corner ticks */}
          <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-widest text-white/60">
            R—01
          </div>
          <div className="absolute bottom-3 right-3 font-mono text-[9px] uppercase tracking-widest text-white/60">
            walk in
          </div>
        </motion.div>
      </div>

      {/* Bottom rolling caption */}
      <motion.div
        style={{ y: captionY }}
        className="absolute bottom-20 left-0 right-0 text-center px-6"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">
          — the showroom is a small orbit in the sky —
        </span>
      </motion.div>
    </section>
  );
}
